import {
  ApplicantOfferRequirementTask,
  ApplicantOfferRequirementTaskLevel,
  ApplicantOfferRequirementTaskStatus,
  ApplicantOfferStatus,
  IApplicantOfferRequirementTask,
  IApplicantOfferRequirement,
  IApplicantOfferAcademicRequirement,
} from 'types';
import { getAllTasksFromRequirements } from '@/utilities/transformRequirements';

type RequirementType = IApplicantOfferRequirement | IApplicantOfferAcademicRequirement;

/**
 * Logic for offer status when issued.
 *
 * Not Satisfied status does not appear here, because if at least one
 * requirement has it, offer issue button is not available.
 *
 * All "Satisfied" or "Exempt" - Issue unconditional offer.
 *
 * At least one "Condition" - Issue conditional offer.
 *
 * All "Assess" - Issue conditional offer.
 *
 * All "Assess" or "Satisfied" or "Exempt" - Issue conditional offer
 *
 */
export function deriveOfferStatus(requirements: RequirementType[]): ApplicantOfferStatus {
  const allTasks = getAllTasksFromRequirements(requirements);

  const relevantTasks = allTasks.filter(
    task =>
      (task.pendingChanges?.level ?? task.level) !== ApplicantOfferRequirementTaskLevel.PreEnrolment &&
      task.type !== ApplicantOfferRequirementTask.StaffOnly,
  );

  const allResolved = relevantTasks.every(task => {
    const status = task.pendingChanges.status ?? task.status;
    return [
      ApplicantOfferRequirementTaskStatus.Satisfied,
      ApplicantOfferRequirementTaskStatus.Provisional,
      ApplicantOfferRequirementTaskStatus.Exempt,
    ].includes(status);
  });

  if (allResolved) return ApplicantOfferStatus.Unconditional;

  return ApplicantOfferStatus.Conditional;
}

/**
 * Legacy function for backward compatibility with flat task arrays
 * @deprecated Use deriveOfferStatus with hierarchical requirements instead
 */
export function deriveOfferStatusFromTasks(tasks: IApplicantOfferRequirementTask[]): ApplicantOfferStatus {
  const allResolved = tasks.every(task => {
    const status = task.pendingChanges.status ?? task.status;
    return [ApplicantOfferRequirementTaskStatus.Satisfied, ApplicantOfferRequirementTaskStatus.Exempt].includes(status);
  });
  if (allResolved) return ApplicantOfferStatus.Unconditional;

  return ApplicantOfferStatus.Conditional;
}
